import { AppFeaturesSystemName, UserListItem } from '@apis/Customers/model';
import { IndexedNotification, Query, UserDefinedNotification } from '@apis/Notification/model';
import { QueryField } from '@apis/Resources';
import styled from '@emotion/styled';
import { Box, Button, Divider, Group, Space, Sx, Text, useMantineTheme } from '@mantine/core';
import { FillerSwitch } from '@root/Design/Filler';
import { EllipsisTextEl } from '@root/Design/Text';
import { AuthenticationService } from '@root/Services/AuthenticationService';
import { AppFeatureNames } from '@root/Services/Customers/CompanyFeatureService';
import { CompanyUserService, ICompanyUserList, UserRecord } from '@root/Services/Customers/CompanyUserService';
import { useDiMemo } from '@root/Services/DI';
import { EventEmitter, useEventValue } from '@root/Services/EventEmitter';
import { FormatService, useFmtSvc } from '@root/Services/FormatService';
import { useNav } from '@root/Services/NavigationService';
import { NotificationService } from '@root/Services/Notification/NotificationService';
import { NotificationViewerService } from '@root/Services/Notification/NotificationViewerService';
import { UserDefinedNotificationApiService } from '@root/Services/Notification/UserDefinedNotificationApiService';
import { IValueProviderFactory } from '@root/Services/Query/QueryDatasource';
import { QueryDatasourceCollection } from '@root/Services/Query/QueryDatasourceCollection';
import { exprBuilder, queryBuilder } from '@root/Services/QueryExpr';
import { useLink } from '@root/Services/Router/Router';
import { ComponentPropsWithoutRef, MouseEventHandler, useCallback, useEffect, useMemo } from 'react';
import { Asterisk, Check, ChevronLeft, ChevronRight, Eye } from 'tabler-icons-react';
import { inject, injectable } from 'tsyringe';
import { DataGrid } from '../DataGrid';
import { DataGridModel } from '../DataGrid/DataGridModel';
import { GridFlexCell } from '../DataGrid/Design';
import { DataGridState, ColumnConfig, IDataSource } from '../DataGrid/Models';
import { GroupRow, QueryExprGroupHelper } from '../DataGrid/QueryExprGroupHelper';
import { useTooltip, FlyoverHost, ITooltipRequest } from '../Picker/Flyover';
import { EmptyDataText } from '../Text/VisibleSpaces';
import { describeComponentType, getComponentTypeDescriptors } from './Components/ComponentDescriptor';
import { describeDatasource, getDatasourceDescriptors } from './Components/DatasourceDescriptor';
import { describeBaseTrigger, describeTrigger } from './Components/Trigger';

export function HistoricalNotificationsGrid() {
    const fmtSvc = useFmtSvc();
    const { colors } = useMantineTheme();
    const model = useDiMemo(HistoricalNotificationsGridModel, [], (m) => m.init());
    useEffect(() => model.dispose, []);
    const { key: tooltipKey, ...tooltipCtrl } = useNotifTooltip();
    const initializing = useEventValue(model.initializing);

    const { columns, state } = useMemo(() => {
        const columns: ColumnConfig<IndexedNotificationModel>[] = [
            {
                accessor: 'OpenedAt',
                id: 'OpenedAt',
                header: 'Seen',
                noRemove: true,
                type: 'date',
                defaultWidth: 60,
                cellRenderer: (v) => <NotificationOpenedCell item={v} tooltip={tooltipCtrl} />,
                filter: true,
            },
            {
                accessor: 'Date',
                id: 'Date',
                header: 'Date',
                noRemove: true,
                defaultWidth: 100,
                type: 'date',
                formatter: (_, v) => fmtSvc.formatMinimalDate(fmtSvc.toLocalDate(v)),
                align: 'center',
                filter: true,
            },
            {
                accessor: 'Title',
                id: 'Title',
                header: 'Title',
                noRemove: true,
                defaultWidth: 200,
                type: 'string',
                filter: {
                    options: model.createValueProvider(),
                    filterType: 'string',
                    filterField: 'Title',
                    name: 'Title',
                },
            },
            {
                accessor: 'Description',
                id: 'Description',
                header: 'Description',
                noRemove: true,
                defaultWidth: 400,
                noSort: true,
                cellRenderer: (v) => <NotificationDescriptionCell item={v} tooltip={tooltipCtrl} />,
                type: 'string',
                filter: {
                    filterType: 'string',
                    filterField: 'Description',
                    name: 'Description',
                },
            },
            {
                accessor: 'TriggerType',
                id: 'TriggerType',
                header: 'Type',
                defaultWidth: 0,
                hideInColumnSelector: true,
                defaultHidden: true,
                type: 'string',
                filter: {
                    options: model.createValueProvider(),
                    filterType: 'string',
                    filterField: 'TriggerType',
                    name: 'Type',
                },
            },
            {
                accessor: 'DatasourceName',
                id: 'DatasourceName',
                header: 'Data',
                defaultWidth: 100,
                hideInColumnSelector: true,
                defaultHidden: true,
                type: 'string',
                filter: {
                    options: model.createValueProvider(),
                    filterType: 'string',
                    filterField: 'DatasourceName',
                    name: 'Data',
                },
            },
            {
                accessor: 'ComponentConfigType',
                id: 'ComponentConfigType',
                header: 'Visual',
                defaultWidth: 0,
                hideInColumnSelector: true,
                defaultHidden: true,
                type: 'string',
                filter: {
                    options: model.createValueProvider(),
                    filterType: 'string',
                    filterField: 'ComponentConfigType',
                    name: 'Visual',
                },
            },
            {
                accessor: 'RecipientUserId',
                id: 'RecipientUserId',
                header: 'Recipient',
                noRemove: true,
                defaultWidth: 130,
                helpText: 'The user who received the notification',
                type: 'string',
                filter: {
                    options: model.createValueProvider(),
                    filterType: 'string',
                    filterField: 'RecipientUserId',
                    name: 'Recipient',
                },
                cellRenderer: (v) => v.recipient?.fullName ?? 'Unavailable',
            },
            {
                accessor: 'OwnerUserId',
                id: 'OwnerUserId',
                header: 'Author',
                noRemove: true,
                defaultWidth: 130,
                helpText: 'The user who manages the notification settings',
                type: 'string',
                filter: {
                    options: model.createValueProvider(),
                    filterType: 'string',
                    filterField: 'OwnerUserId',
                    name: 'Author',
                },
                cellRenderer: (v) => v.owner?.fullName ?? 'Unavailable',
            },
        ];

        const { builder: xb } = exprBuilder<IndexedNotification>();
        const currUser = model.getRecipientById(model.userId);
        const currUserCrit = !currUser ? null : xb.resolve(xb.model.RecipientUserId!.eq([currUser.value]));
        const state: DataGridState = {
            sort: [{ Expr: { Field: 'Date' }, Direction: 'Desc' }],
            filters: !currUserCrit ? [] : [currUserCrit],
            columns: columns.filter((c) => !c.defaultHidden).map((c) => ({ id: c.id, width: c.defaultWidth })),
        };

        return { state, columns };
    }, [initializing]);

    return (
        <Box sx={{ height: '100%', overflow: 'hidden' }} onMouseLeave={tooltipCtrl.hide}>
            <FillerSwitch loading={initializing || !columns.length}>
                {() => (
                    <DataGrid
                        allowSavedViews
                        statePersistence={{ key: 'HistNotifications' }}
                        state={state}
                        dataSource={model.dataSource}
                        columns={columns}
                        onModelLoaded={model.attachGrid}
                        onRowClick={model.handleRowClick}
                        showRefresh
                        highlightColor={colors.warning[2]}
                        rightTopPlaceHolder={<ManageNotificationButton />}
                        leftFilterPlaceHolder={<ManageNotificationButton left />}
                        hideColumnSelector
                    />
                )}
            </FillerSwitch>
            <FlyoverHost flyoverKey={tooltipKey} routeBound />
        </Box>
    );
}

function ManageNotificationButton({ left }: { left?: boolean }) {
    const { getDescendUrl, getParentRoute, getAscendUrl } = useNav();
    const link = useLink();
    const parentRoute = getParentRoute();
    const fromSettings = parentRoute?.name?.toLowerCase() === 'notification-settings';
    const url = fromSettings ? getAscendUrl() : getDescendUrl('notification-settings');
    const icons = {
        leftIcon: fromSettings ? <ChevronLeft size={16} /> : undefined,
        rightIcon: !fromSettings ? <ChevronRight size={16} /> : undefined,
    };

    return (left && fromSettings) || (!left && !fromSettings) ? (
        <>
            {!left ? <Space w="md" /> : null}
            <Button variant="outline" my={5} component="a" size="xs" radius="lg" {...icons} {...link(url)}>
                {fromSettings ? 'Back' : 'Manage Notifications'}
            </Button>
            {left ? <Space w="sm" /> : null}
        </>
    ) : (
        <></>
    );
}

interface IndexedNotificationModel extends IndexedNotification {
    component: ReturnType<typeof describeComponentType>;
    trigger: ReturnType<typeof describeBaseTrigger>;
    datasource: ReturnType<typeof describeDatasource>;
    recipient?: UserRecord;
    owner?: UserRecord;
}

function useNotifTooltip() {
    const key = 'HistoricalNotificationsGrid';
    const [show, hide] = useTooltip({ nonInteractive: true, anchor: 'top-center', offsetY: 15 }, key);
    return useMemo(() => ({ show, hide, key }), []);
}

@injectable()
class HistoricalNotificationsGridModel {
    private gridQueryHelper!: QueryExprGroupHelper<IndexedNotification>;
    private recipients: { label: string; value: number }[] = [];
    private owners: { label: string; value: number }[] = [];
    private loadSelectedItem: IndexedNotificationModel | null = null;
    private pickerItemLookup: Map<number, { value: number; label: string }> = new Map();
    private readonly disposers: (() => void)[] = [];
    public users?: ICompanyUserList;
    public readonly initializing = new EventEmitter<boolean>(true);
    public get userId() {
        return this.authNSvc.user?.Id ?? 0;
    }

    public constructor(
        @inject(CompanyUserService) private readonly userSvc: CompanyUserService,
        @inject(UserDefinedNotificationApiService) private readonly notificationApi: UserDefinedNotificationApiService,
        @inject(AuthenticationService) private readonly authNSvc: AuthenticationService,
        @inject(QueryDatasourceCollection) private readonly queryDatasources: QueryDatasourceCollection,
        @inject(FormatService) private readonly fmtSvc: FormatService,
        @inject(NotificationViewerService) private readonly viewerSvc: NotificationViewerService
    ) {}

    public init() {
        this.initialize();
        return this;
    }

    public getRecipientById(id: number) {
        return this.recipients.find((r) => r.value === id);
    }

    private async initialize() {
        this.initializing.emit(true);
        try {
            const { dispose } = this.viewerSvc.onRead.listen(this.handleNotificationRead);
            this.disposers.push(dispose);

            const queryApi = (q: Query) => this.notificationApi.queryNotifications(q);
            this.gridQueryHelper = new QueryExprGroupHelper(queryApi, queryApi, undefined, undefined, [
                { Direction: 'Desc', Expr: { Field: 'Date' } },
            ]);

            this.users = await this.userSvc.getUserList();
            this.pickerItemLookup = this.users
                .getList()
                .reduce((res, u) => res.set(u!.Id, { label: u!.fullName, value: u!.Id }), new Map<number, { value: number; label: string }>());
            const [recipients, owners] = await Promise.all([this.getUserLabels('RecipientUserId'), this.getUserLabels('OwnerUserId')]);
            this.recipients = recipients;
            this.owners = owners;
        } finally {
            this.initializing.emit(false);
        }
    }

    private async getUserLabels(field: 'RecipientUserId' | 'OwnerUserId') {
        const validValuesProvider = this.notificationApi.queryFieldValues<number>(field);
        const validValues = await validValuesProvider();
        const users = validValues.map((v) => this.users!.getById(v));
        const result = users
            .filter((u) => !!u)
            .sort((a, b) => (a?.formalSortableName ?? '').localeCompare(b?.formalSortableName ?? ''))
            .map((u) => this.pickerItemLookup.get(u?.Id ?? 0)!)
            .filter((u) => !!u);

        return result;
    }

    public get dataSource(): IDataSource<IndexedNotificationModel> {
        return {
            getPage: async (...args) => {
                const result = await this.gridQueryHelper.getPage(...args);
                const items =
                    result?.items?.map((item) => {
                        if ('groupId' in item) {
                            return item;
                        } else {
                            return this.enrichNotification(item);
                        }
                    }) ?? [];
                return { total: result.total ?? 0, items };
            },
        };
    }

    public dispose = () => {
        this.disposers.forEach((d) => d());
    };

    public attachGrid = (grid: DataGridModel) => {
        this.gridQueryHelper.attach(grid);
    };

    public handleRowClick = (item: IndexedNotificationModel) => {
        this.loadSelectedItem = item;
        this.viewerSvc.view(item.NotificationId ?? 0, item.RecipientUserId);
    };

    public createValueProvider(): IValueProviderFactory {
        return {
            getValueProvider: (field: QueryField) => {
                switch (field.Field) {
                    case 'RecipientUserId':
                        return this.recipients;
                    case 'OwnerUserId':
                        return this.owners;
                    case 'TriggerType':
                        return [
                            { label: 'Scheduled Report', value: 'Schedule' },
                            { label: 'Alert', value: 'Threshold' },
                        ];
                    case 'ComponentConfigType':
                        return getComponentTypeDescriptors().map((d) => ({ label: d.description, value: d.subType }));
                    case 'DatasourceName':
                        return getDatasourceDescriptors().map((d) => ({ label: d.name, value: d.dsName }));
                    case 'Title':
                        return this.notificationApi.queryFieldValues<string>('Title');
                    default:
                        throw new Error(`Unknown field: ${field.Field}`);
                }
            },
        };
    }

    private handleNotificationRead = (notification: UserDefinedNotification | undefined) => {
        const grid = this.gridQueryHelper.getAttachedGrid();
        const itemModel = this.loadSelectedItem;
        if (grid && itemModel && notification?.Id === itemModel.NotificationId) {
            itemModel.OpenedAt = notification?.OpenedAt;
            grid.treeModel?.invalidateItem(itemModel);
        }
    };

    private enrichNotification(item: IndexedNotification): IndexedNotificationModel {
        const component = describeComponentType(item.ComponentType, item.ComponentConfigType);
        const trigger = describeBaseTrigger(item.TriggerType as 'Threshold' | 'Schedule' | undefined);
        const ds = this.queryDatasources.getDatasourceMeta(item.DatasourceName ?? '');
        const datasource = describeDatasource(ds ?? { name: item.DatasourceName ?? '', feature: AppFeatureNames.None });

        return {
            ...item,
            component,
            trigger,
            datasource,
            recipient: this.users?.getById(item.RecipientUserId ?? 0),
            owner: this.users?.getById(item.OwnerUserId ?? 0),
        };
    }
}

function NotificationOpenedCell({ item, tooltip }: { item: IndexedNotificationModel; tooltip: ITooltipControls }) {
    const { colors } = useMantineTheme();
    const fmtSvc = useFmtSvc();
    const icon = item.OpenedAt ? (
        <Check size={16} stroke={colors.primary[6]} strokeWidth={2} />
    ) : (
        <Asterisk size={16} stroke={colors.warning[6]} strokeWidth={2} />
    );
    const handleMouseEnter: MouseEventHandler<HTMLDivElement> = useCallback(
        (e) => {
            const box = e.currentTarget.getBoundingClientRect();
            const x = box.left + box.width / 2;
            const y = box.top + box.height / 2;
            tooltip.show({
                x,
                y,
                renderer: () => {
                    const when = item.OpenedAt ? fmtSvc.timeAgo(fmtSvc.toLocalDate(item.OpenedAt)) : null;
                    return (
                        <TooltipContainer>
                            <Box py={3} px={6}>
                                {!item.OpenedAt ? (
                                    <Text size="sm">Not seen by recipient</Text>
                                ) : (
                                    <>
                                        <Text size="xs">Seen by recipient</Text>
                                        <Text size="sm">{when}</Text>
                                    </>
                                )}
                            </Box>
                        </TooltipContainer>
                    );
                },
            });
        },
        [item.OpenedAt, tooltip]
    );
    return (
        <GridFlexCell align="center" onMouseEnter={handleMouseEnter} onMouseLeave={tooltip.hide}>
            {icon}
        </GridFlexCell>
    );
}

interface ITooltipControls {
    show: (request: ITooltipRequest) => void;
    hide: () => void;
}

function NotificationDescriptionCell({ item, tooltip }: { item: IndexedNotificationModel; tooltip: ITooltipControls }) {
    const { colors } = useMantineTheme();
    const icons = [item.trigger.icon, item.datasource.icon, item.component.icon].map((Ico, i) => <Ico key={i} size={16} stroke={colors.gray[6]} />);
    const description = item.Description?.trim() === '' ? <EmptyDataText text="Description Empty" /> : item.Description;

    const handleMouseEnter: MouseEventHandler<HTMLDivElement> = useCallback(
        (e) => {
            const box = e.currentTarget.getBoundingClientRect();
            const x = box.left + box.width / 2;
            const y = box.top + box.height / 2;
            tooltip.show({
                x,
                y,
                renderer: () => {
                    return <NotificationDescriptionTooltip item={item} />;
                },
            });
        },
        [item.Id, tooltip]
    );

    return (
        <GridFlexCell onMouseEnter={handleMouseEnter} onMouseLeave={tooltip.hide} style={{ gap: 4 }}>
            <Text component={EllipsisTextEl} style={{ flex: 1, maxWidth: '100%' }} size="sm">
                {description}
            </Text>
            {icons}
        </GridFlexCell>
    );
}

function NotificationDescriptionTooltip({ item }: { item: IndexedNotificationModel }) {
    const { colors } = useMantineTheme();
    const { trigger, datasource, component } = item;
    const { type: triggerLbl, icon: TriggerIcon } = trigger;
    const { name: dsLbl, icon: DsIcon } = datasource;
    const { description: visLbl, icon: VisIcon } = component;
    const iconProps = { size: 16, stroke: colors.gray[5] };
    const textProps: ComponentPropsWithoutRef<typeof Text> = { size: 13, color: 'gray.7', span: true, sx: { whiteSpace: 'nowrap' } };

    return (
        <TooltipContainer style={{ width: 500, maxHeight: 150, overflow: 'hidden' }}>
            <Group px={8} py={4} noWrap position="left" spacing={3}>
                (<TriggerIcon {...iconProps} />)<Text {...textProps}>{`${triggerLbl} of`}</Text>
                (<DsIcon {...iconProps} />)<Text {...textProps}>{`${dsLbl} displayed as`}</Text>
                (<VisIcon {...iconProps} />)<Text {...textProps}>{visLbl}</Text>
            </Group>
            <Divider color="gray.3" />
            <Text px={8} py={4} sx={{ whiteSpace: 'pre-wrap' }} size={13}>
                {item.Description || <EmptyDataText text="Description Empty" />}
            </Text>
        </TooltipContainer>
    );
}

const TooltipContainer = styled.div`
    background: ${(p) => p.theme.colors.gray[0]}e0;
    border-radius: 3px;
    border: solid 1px ${(p) => p.theme.colors.gray[3]};
    box-shadow: ${(p) => p.theme.shadows.sm};
`;
